/**
 * SEO Regression Dashboard — admin-only view of the monthly automated SEO
 * crawl report. Runs on the 1st of every month at 08:00 UTC via the
 * `seo_regression_monthly_loop` cron, but can also be triggered manually.
 *
 * Backend: GET  /api/seo-regression/latest  — most recent report
 *          POST /api/seo-regression/run     — manual run (also admin-only)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, RefreshCw, PlayCircle, ShieldAlert } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import usePageSeo from "@/hooks/usePageSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHECK_LABEL = {
  status:            "HTTP 200",
  title:             "<title> present",
  description:       "Meta description (no truncation)",
  canonical:         "Self-referential canonical",
  robots_indexable:  "robots not noindex",
  h1_single:         "Exactly one <h1>",
  schema:            "JSON-LD schema present",
  hreflang:          "hreflang alternates",
  fetch_error:       "Fetch error",
};

export default function SeoRegressionDashboard() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  usePageSeo({
    title: "SEO Regression Dashboard | WebsiteTech.pro",
    description: "Automated monthly SEO regression report — status, canonicals, titles, descriptions, H1s, schema and hreflang.",
    noindex: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("wt_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.get(`${API}/seo-regression/latest`, { headers });
      setReport(data?.report || null);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 403) toast.error("Admin only");
      else if (code === 401) toast.error("Please log in as admin");
      else toast.error("Could not load latest regression");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const token = localStorage.getItem("wt_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.post(`${API}/seo-regression/run`, {}, { headers });
      toast.success(`Regression run complete: ${data?.report?.passed}/${data?.report?.total_routes} passed`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally { setRunning(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="pt-28 pb-20" data-testid="seo-regression-dashboard">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand flex items-center gap-2">
                <ShieldAlert size={13} /> SEO Regression Monitor
              </p>
              <h1 className="mt-2 font-heading font-extrabold text-slate-900 text-4xl sm:text-5xl leading-tight">
                Catch SEO regressions<br />
                <span className="text-brand">before customers do.</span>
              </h1>
              <p className="mt-3 text-base text-slate-600 max-w-2xl">
                Automated crawl runs on the 1st of every month at 08:00 UTC. Each monitored route is
                validated against: HTTP 200, title, description, self-canonical, single H1, JSON-LD schema,
                hreflang, and indexable robots. Red = regression.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                data-testid="seo-regression-refresh"
                onClick={load}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-brand"
              >
                <RefreshCw size={14} /> Refresh
              </button>
              <button
                data-testid="seo-regression-run-now"
                onClick={runNow}
                disabled={running}
                className="btn-primary rounded-lg px-4 py-2 text-sm font-semibold flex items-center gap-1.5 disabled:opacity-60"
              >
                {running ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
                Run now
              </button>
            </div>
          </div>

          {loading && !report && (
            <div className="mt-10 bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-400">
              <Loader2 size={20} className="mx-auto animate-spin" />
              <p className="mt-2 text-sm">Loading report…</p>
            </div>
          )}

          {!loading && !report && (
            <div className="mt-10 bg-white border border-slate-200 rounded-xl p-10 text-center">
              <ShieldAlert size={28} className="mx-auto text-amber-500" />
              <h3 className="mt-3 font-bold text-slate-900">No regression runs yet.</h3>
              <p className="mt-1 text-sm text-slate-600">Click <strong>Run now</strong> above to generate the first baseline.</p>
            </div>
          )}

          {report && (
            <>
              <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Routes crawled</div>
                  <div className="mt-1 text-2xl font-extrabold text-slate-900">{report.total_routes}</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold">Passed</div>
                  <div className="mt-1 text-2xl font-extrabold text-emerald-800">{report.passed}</div>
                </div>
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                  <div className="text-[11px] uppercase tracking-wider text-rose-700 font-bold">Failed</div>
                  <div className="mt-1 text-2xl font-extrabold text-rose-800">{report.failed}</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Started</div>
                  <div className="mt-1 text-xs font-mono text-slate-700 break-all">{report.started_at?.slice(0, 19)}Z</div>
                </div>
              </div>

              {report.fail_summary?.length > 0 && (
                <section className="mt-8" data-testid="seo-regression-fail-summary">
                  <h2 className="font-heading font-bold text-slate-900 text-xl">Regressions this run</h2>
                  <div className="mt-3 bg-white border border-rose-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-rose-50 text-[11px] uppercase tracking-wider text-rose-800">
                        <tr>
                          <th className="text-left px-4 py-2 font-semibold">Path</th>
                          <th className="text-left px-4 py-2 font-semibold">Failed checks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.fail_summary.map((f, i) => (
                          <tr key={i} className="border-t border-rose-100">
                            <td className="px-4 py-2 font-mono text-xs text-slate-800">{f.path}</td>
                            <td className="px-4 py-2 flex flex-wrap gap-1">
                              {f.failed_checks.map((c) => (
                                <span key={c} className="text-[10px] font-bold uppercase bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full">
                                  {CHECK_LABEL[c] || c}
                                </span>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {report.results && (
                <section className="mt-8">
                  <h2 className="font-heading font-bold text-slate-900 text-xl">All routes</h2>
                  <div className="mt-3 bg-white border border-slate-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="text-left px-4 py-2 font-semibold">Path</th>
                          <th className="text-left px-4 py-2 font-semibold">Checks</th>
                          <th className="text-right px-4 py-2 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.results.map((r, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-4 py-2 font-mono text-xs text-slate-800">{r.path}</td>
                            <td className="px-4 py-2 flex flex-wrap gap-1">
                              {Object.entries(r.checks).map(([k, v]) => (
                                <span key={k} title={CHECK_LABEL[k] || k} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${v.ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                                  {v.ok ? "✓" : "✗"} {k.replace(/_/g, " ")}
                                </span>
                              ))}
                            </td>
                            <td className="text-right px-4 py-2">
                              {r.passed ? <CheckCircle2 size={16} className="text-emerald-600 inline" /> : <XCircle size={16} className="text-rose-600 inline" />}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
